(() => {
  const mobile = window.matchMedia('(max-width: 749px)');

  const normalize = (value) => value.trim().toLowerCase();

  const visualOptionNames = new Set(['style', 'set style', 'design', 'colour', 'color', 'charm style']);

  let initializeQueued = false;

  const queueInitialize = () => {
    if (initializeQueued) return;
    initializeQueued = true;
    window.requestAnimationFrame(() => {
      initializeQueued = false;
      initialize();
    });
  };

  const enhanceVisualOptions = (root, context) => {
    const mediaItems = [...context.querySelectorAll('[data-pml-option-media]')];
    root.querySelectorAll('input[data-option-name]').forEach((input) => {
      const optionName = normalize(input.dataset.optionName || '');
      if (!visualOptionNames.has(optionName)) return;
      const label = root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;
      const nativeImage = [...label.querySelectorAll(':scope > img')].find((image) => (
        !image.hasAttribute('data-pml-visual-thumbnail')
      ));
      if (nativeImage) {
        label.querySelectorAll(':scope > img[data-pml-visual-thumbnail]').forEach((thumbnail) => thumbnail.remove());
        nativeImage.classList.add('pml-visual-thumbnail');
        label.classList.add('pml-native-visual-option');
        label.closest('fieldset')?.classList.add('pml-native-visual-picker');
        return;
      }
      const item = mediaItems.find((candidate) => (
        normalize(candidate.dataset.optionName || '') === optionName
        && candidate.dataset.optionValue === input.value
      ));
      if (!item?.dataset.optionImage) return;
      const existing = label.querySelector(':scope > img[data-pml-visual-thumbnail]');
      if (existing?.src === new URL(item.dataset.optionImage, window.location.origin).href) {
        label.classList.add('pml-native-visual-option');
        label.closest('fieldset')?.classList.add('pml-native-visual-picker');
        return;
      }
      label.querySelectorAll(':scope > img[data-pml-visual-thumbnail]').forEach((thumbnail) => thumbnail.remove());
      const image = document.createElement('img');
      image.src = item.dataset.optionImage;
      image.alt = '';
      image.width = 160;
      image.height = 160;
      image.loading = 'lazy';
      image.dataset.pmlVisualThumbnail = '';
      image.classList.add('pml-visual-thumbnail');
      label.prepend(image);
      label.classList.add('pml-native-visual-option');
      label.closest('fieldset')?.classList.add('pml-native-visual-picker');
    });
  };

  const prepareAccessoryMaterials = (root, context) => {
    if (context.dataset.pmlMaterialsPrepared) return;
    const type = normalize(context.dataset.productType || '');
    if (!type.includes('charm') && !type.includes('chain')) return;
    const accordions = [...root.querySelectorAll('.product__accordion')];
    const target = accordions.find((item) => normalize(item.querySelector('.accordion__title')?.textContent || '').includes('size'));
    if (!target) return;
    const title = target.querySelector('.accordion__title');
    const content = target.querySelector('.accordion__content');
    const materials = context.querySelector('[data-pml-materials]');
    title.textContent = 'Materials';
    content.replaceChildren(...(materials?.content?.cloneNode(true).childNodes || []));
    context.dataset.pmlMaterialsPrepared = 'true';
  };

  const activate = (tabs, index, focus = false) => {
    tabs.buttons.forEach((button, buttonIndex) => {
      const active = buttonIndex === index;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
      tabs.panels[buttonIndex].hidden = !active;
    });
    tabs.buttons[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    if (focus) tabs.buttons[index].focus();
  };

  const buildTabs = (root) => {
    if (root.querySelector('.pml-product-tabs')) return;
    const accordions = [...root.querySelectorAll('.product__accordion')];
    if (accordions.length < 2) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'pml-product-tabs';
    const list = document.createElement('div');
    list.className = 'pml-product-tabs__list';
    list.setAttribute('role', 'tablist');
    const panels = [];
    const buttons = [];

    accordions.forEach((accordion, index) => {
      const title = accordion.querySelector('.accordion__title')?.textContent?.trim() || `Tab ${index + 1}`;
      const source = accordion.querySelector('.accordion__content');
      const button = document.createElement('button');
      const panel = document.createElement('div');
      button.type = 'button';
      button.className = 'pml-product-tabs__tab';
      button.id = `pml-tab-${index}-${Math.random().toString(36).slice(2, 8)}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', `${button.id}-panel`);
      button.textContent = title.replace('Design & Details', 'Details').replace('Shipping & Returns', 'Shipping');
      panel.className = 'pml-product-tabs__panel rte';
      panel.id = `${button.id}-panel`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', button.id);
      panel.append(...source.cloneNode(true).childNodes);
      button.addEventListener('click', () => activate({ buttons, panels }, index));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = buttons.length - 1;
        activate({ buttons, panels }, next, true);
      });
      list.append(button);
      wrapper.append(panel);
      buttons.push(button);
      panels.push(panel);
    });

    wrapper.prepend(list);
    const accordionAnchor = accordions[0];
    accordionAnchor.before(wrapper);
    accordions.forEach((accordion) => {
      accordion.dataset.pmlOriginalDisplay = accordion.style.display;
      accordion.style.display = 'none';
    });
    activate({ buttons, panels }, 0);
  };

  const removeTabs = (root) => {
    root.querySelector('.pml-product-tabs')?.remove();
    root.querySelectorAll('.product__accordion[data-pml-original-display]').forEach((accordion) => {
      accordion.style.display = accordion.dataset.pmlOriginalDisplay;
      delete accordion.dataset.pmlOriginalDisplay;
    });
  };

  const initialize = () => {
    document.querySelectorAll('[data-pml-product-context]').forEach((context) => {
      const root = context.closest('.product__info-container');
      if (!root) return;
      prepareAccessoryMaterials(root, context);
      enhanceVisualOptions(root, context);
      if (mobile.matches) buildTabs(root);
      else removeTabs(root);
    });

    document.querySelectorAll('main[data-template="index"] [id$="__trust"] .multicolumn-card').forEach((card) => {
      const link = card.querySelector('a[href]');
      if (!link || card.dataset.pmlClickable) return;
      card.dataset.pmlClickable = 'true';
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, input')) return;
        link.click();
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  mobile.addEventListener('change', queueInitialize);
  document.addEventListener('shopify:section:load', queueInitialize);
  document.addEventListener('change', (event) => {
    if (event.target.closest('.product__info-container')) queueInitialize();
  });
  new MutationObserver(queueInitialize).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

